import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * ClampWithMore — displays `text` clamped to `lines` lines (CSS webkit-line-clamp)
 * and shows a "Read more" / "Show less" toggle when the content actually overflows.
 * Used across the app on every listing card (directory, events, coupons, loyalty,
 * auction, deal bin, store, news) for a uniform description experience.
 */
export default function ClampWithMore({
  text,
  lines = 3,
  className = 'text-xs text-gray-400 mt-2',
  moreLabel = 'Read more',
  lessLabel = 'Show less',
  testId,
}) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Only show the toggle if the collapsed text actually overflows.
  useLayoutEffect(() => {
    if (!ref.current) return;
    // Force measure the clamped state to know whether "more" is needed.
    const el = ref.current;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines]);

  // Re-measure on window resize (font/line reflow).
  useEffect(() => {
    const onResize = () => {
      if (!ref.current) return;
      if (expanded) return; // measurement only meaningful in collapsed state
      setOverflows(ref.current.scrollHeight > ref.current.clientHeight + 1);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [expanded]);

  if (!text) return null;

  const clampStyle = !expanded
    ? {
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }
    : undefined;

  return (
    <div className={className} data-testid={testId}>
      <div ref={ref} style={clampStyle}>{text}</div>
      {overflows && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpanded(v => !v); }}
          className="mt-1 text-[11px] font-semibold text-green-400 hover:text-green-300 underline-offset-2 hover:underline"
          data-testid={testId ? `${testId}-toggle` : 'clamp-toggle'}
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  );
}
